/**
 * TenantTree — hierarchical tenant picker for the Design System gallery.
 *
 * UX rules baked in:
 *   • Root tenants are EXPANDED by default so the user sees structure (one
 *     layer of children) the moment the picker opens — no chevron-hunting
 *     just to find out the tree has depth. Grandchildren stay collapsed
 *     unless the user drills in.
 *   • Global filter input — searches every node at every depth and, when
 *     active, switches to a flat list of just the matches.
 *   • No "Expand all / Collapse all" buttons — those were power-user noise
 *     for the picker context. The chevrons on each row handle the depth.
 *   • The list scrolls INTERNALLY so a 50-tenant tree never blows out the
 *     picker modal's height. A bottom fade-out hints there's more below.
 *
 * Click anywhere on the row body → onSelect(tenant_id). Kept tightly
 * scoped — this is a picker, not a tenant manager.
 */

import { useMemo, useState } from 'react';
import { useLanguage } from '@/hooks/useLanguage';
import { Input, Tag } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { defaultBrand } from '@/brand';
import { mergeBrandOverrides } from '@/brand/merge-overrides';
import { getDsTreeExpanded, setDsTreeExpanded } from '@modules/super_admin/console/consoleState';

export interface TenantNode {
  tenant_id: string;
  tenant_name: string;
  tenant_code: string;
  parent_tenant_id?: string | null;
  /** Brand overrides for this tenant — used to show a real swatch + font name. */
  brandOverrides?: any;
}

interface Props {
  tenants: TenantNode[];
  ownTenantId?: string | null;
  onSelect: (tenantId: string) => void;
  /** Optional className for extra styling at the wrapper. */
  className?: string;
}

interface TreeRow {
  node: TenantNode;
  depth: number;
  childCount: number;
}

export default function TenantTree({ tenants, ownTenantId, onSelect, className }: Props) {
  const { t } = useLanguage();
  const [query, setQuery] = useState('');
  // `expanded === null` means we haven't initialized yet — useMemo below
  // seeds it from the tenant list (auto-expand roots that have children).
  // The set survives navigation within the session (sessionStorage), so
  // coming Back from an editor returns to the same open branches instead
  // of a fully re-expanded tree.
  const [expanded, setExpandedState] = useState<Set<string> | null>(() => {
    const ids = getDsTreeExpanded();
    return ids === null ? null : new Set(ids);
  });
  const setExpanded = (updater: (prev: Set<string> | null) => Set<string>) => {
    setExpandedState(prev => {
      const next = updater(prev);
      setDsTreeExpanded([...next]);
      return next;
    });
  };

  // Build adjacency: parent_tenant_id → [child]. Tenants with no/missing
  // parent are roots. Cycles are impossible per DB constraint.
  const { roots, childrenOf } = useMemo(() => {
    const childrenOf: Record<string, TenantNode[]> = {};
    const known: Set<string> = new Set();
    for (const t of tenants) known.add(t.tenant_id);
    const roots: TenantNode[] = [];
    for (const t of tenants) {
      const parent = t.parent_tenant_id ?? null;
      if (!parent || !known.has(parent)) {
        roots.push(t);
      } else {
        (childrenOf[parent] ||= []).push(t);
      }
    }
    const byName = (a: TenantNode, b: TenantNode) =>
      a.tenant_name.localeCompare(b.tenant_name);
    roots.sort(byName);
    Object.values(childrenOf).forEach(arr => arr.sort(byName));
    return { roots, childrenOf };
  }, [tenants]);

  // Seed expanded set once roots are known: every root that actually has
  // children starts expanded. Re-seed if the tenants list changes shape
  // (e.g. a new tenant lands or the user navigates between databases).
  const initialExpanded = useMemo(() => {
    const s = new Set<string>();
    for (const r of roots) {
      if ((childrenOf[r.tenant_id] ?? []).length > 0) s.add(r.tenant_id);
    }
    return s;
  }, [roots, childrenOf]);

  const effectiveExpanded = expanded ?? initialExpanded;

  // Walk the tree → ordered list of rows respecting expanded state.
  const rows: TreeRow[] = useMemo(() => {
    const out: TreeRow[] = [];
    function walk(node: TenantNode, depth: number) {
      const kids = childrenOf[node.tenant_id] ?? [];
      out.push({ node, depth, childCount: kids.length });
      if (effectiveExpanded.has(node.tenant_id)) {
        for (const k of kids) walk(k, depth + 1);
      }
    }
    for (const r of roots) walk(r, 0);
    return out;
  }, [roots, childrenOf, effectiveExpanded]);

  // Filtered (flat) view when query is set. Searches name + code at EVERY
  // depth, not just the visible level.
  const filteredFlat: TreeRow[] = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];
    const out: TreeRow[] = [];
    for (const t of tenants) {
      if (
        t.tenant_name.toLowerCase().includes(q) ||
        t.tenant_code.toLowerCase().includes(q)
      ) {
        // depth 0 — flat layout when filtering, no indentation distractions
        out.push({ node: t, depth: 0, childCount: (childrenOf[t.tenant_id] ?? []).length });
      }
    }
    return out;
  }, [query, tenants, childrenOf]);

  const isFiltering = query.trim().length > 0;
  const visibleRows = isFiltering ? filteredFlat : rows;

  function toggle(id: string) {
    setExpanded(prev => {
      const next = new Set(prev ?? initialExpanded);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const totalCount = tenants.length;
  const matchCount = visibleRows.length;

  return (
    <div className={`ds-tt ${className ?? ''}`}>
      <header className="ds-tt-header">
        <div className="ds-tt-header-text">
          <div className="ds-tt-sub">
            {isFiltering
              ? `${matchCount} of ${totalCount} match`
              : t('counts.tenantsClickRow', { count: totalCount })}
          </div>
        </div>
        <div className="ds-tt-tools">
          <Input dir="auto"
            allowClear
            prefix={<SearchOutlined className="ds-tt-search-icon" />}
            placeholder={t('common.searchTenants')}
            value={query}
            onChange={e => setQuery(e.target.value)}
            className="ds-tt-search"
            data-testid="tenant-tree-search"
          />
        </div>
      </header>

      {/* Scroll surface — the list scrolls INSIDE this wrapper so the modal
          stays a fixed size. The bottom fade (::after on the wrapper) is a
          visual cue that more rows live below the viewport when the list
          overflows. */}
      <div className="ds-tt-list-wrap">
        <ul className="ds-tt-list" role="tree" data-testid="tenant-tree">
          {visibleRows.length === 0 && (
            <li className="ds-tt-empty">
              {tenants.length === 0
                ? t('superAdmin.loadingTenants')
                : `No tenants match “${query}”.`}
            </li>
          )}
          {visibleRows.map(({ node, depth, childCount }) => {
            const merged = mergeBrandOverrides(defaultBrand, node.brandOverrides || {});
            const isExpanded = effectiveExpanded.has(node.tenant_id);
            const isOwn = node.tenant_id === ownTenantId;
            const hasChildren = childCount > 0;
            return (
              <li
                key={node.tenant_id}
                className="ds-tt-row"
                data-testid={`tenant-row-${node.tenant_code}`}
                style={{ paddingInlineStart: `${depth * 22 + 12}px` }}
                role="treeitem"
                aria-level={depth + 1}
                aria-expanded={hasChildren ? isExpanded : undefined}
              >
                <button
                  type="button"
                  className="ds-tt-chevron"
                  onClick={(e) => { e.stopPropagation(); if (hasChildren) toggle(node.tenant_id); }}
                  aria-label={isExpanded ? t('common.collapse') : t('common.expand')}
                  aria-expanded={hasChildren ? isExpanded : undefined}
                  disabled={!hasChildren}
                  title={hasChildren ? (isExpanded ? t('common.collapse') : t('designSystem.expandSubTenants')) : undefined}
                >
                  {hasChildren && !isFiltering && (
                    <span className="ds-tt-chevron-icon">▶</span>
                  )}
                </button>
                <button
                  type="button"
                  className="ds-tt-row-body"
                  onClick={() => onSelect(node.tenant_id)}
                  title={`Open White-Label Editor for ${node.tenant_name}`}
                >
                  <span
                    className="ds-tt-swatch"
                    style={{ background: merged.colors.gradientPrimary }}
                    aria-hidden
                  >
                    {merged.icon && <img src={merged.icon} alt="" />}
                  </span>
                  <span className="ds-tt-row-text">
                    <span className="ds-tt-row-name">
                      {node.tenant_name}
                      {isOwn && <Tag color="blue" style={{ marginInlineStart: 6 }}>{t('superAdmin.yourTenant')}</Tag>}
                    </span>
                    <span className="ds-tt-row-meta">
                      <span className="ds-tt-row-code">{node.tenant_code}</span>
                      {hasChildren && (
                        <>
                          <span className="ds-tt-dot" />
                          <span className="ds-tt-row-children">
                            {t('counts.subTenants', { count: childCount })}
                          </span>
                        </>
                      )}
                    </span>
                  </span>
                  <span className="ds-tt-row-cta">Design →</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
}
